import React, { useState } from 'react';
import {
  QrCode,
  ScanLine,
  History,
  Layers,
  LayoutTemplate,
  Wrench,
  Settings,
  MoreHorizontal,
  X
} from 'lucide-react';

interface MobileBottomNavProps {
  activeTab: string;
  onSelectTab: (tab: string) => void;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ activeTab, onSelectTab }) => {
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const primaryTabs = [
    { id: 'generator', label: 'Create', icon: <QrCode className="h-5 w-5" /> },
    { id: 'scanner', label: 'Scan', icon: <ScanLine className="h-5 w-5" /> },
    { id: 'templates', label: 'Templates', icon: <LayoutTemplate className="h-5 w-5" /> },
    { id: 'history', label: 'History', icon: <History className="h-5 w-5" /> }
  ];

  const moreItems = [
    { id: 'batch', label: 'Batch Generator', desc: 'Create bulk QR codes from CSV', icon: <Layers className="h-5 w-5 text-primary" /> },
    { id: 'tools', label: 'Diagnostics & Tools', desc: 'Print size calculator & contrast', icon: <Wrench className="h-5 w-5 text-primary" /> },
    { id: 'settings', label: 'Settings & Privacy', desc: 'Defaults & storage', icon: <Settings className="h-5 w-5 text-muted-foreground" /> }
  ];

  const isMoreActive = ['batch', 'tools', 'settings'].includes(activeTab);

  const handleTabClick = (id: string) => {
    onSelectTab(id);
    setIsMoreOpen(false);
  };

  return (
    <>
      {/* More Options Bottom Action Sheet on Mobile */}
      {isMoreOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex flex-col justify-end">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
            onClick={() => setIsMoreOpen(false)}
          />

          {/* Action Sheet Container */}
          <div className="relative z-10 w-full bg-card rounded-t-3xl border-t border-border p-5 pb-[max(2rem,env(safe-area-inset-bottom))] shadow-2xl space-y-3 animate-in slide-in-from-bottom duration-200">
            <div className="flex items-center justify-between pb-2 border-b border-border">
              <span className="text-[15px] font-semibold text-foreground tracking-tight">
                More Features
              </span>
              <button
                onClick={() => setIsMoreOpen(false)}
                className="p-1 text-muted-foreground hover:text-foreground rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-2 pt-1">
              {moreItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleTabClick(item.id)}
                  className={`flex items-center gap-3.5 p-3 rounded-xl text-left transition-colors cursor-pointer active:scale-[0.98] ${
                    activeTab === item.id
                      ? 'bg-primary/10 text-primary'
                      : 'bg-muted text-foreground'
                  }`}
                >
                  <div className="p-2 rounded-lg bg-card shadow-xs">
                    {item.icon}
                  </div>
                  <div>
                    <h4 className="text-[14px] font-semibold tracking-tight">{item.label}</h4>
                    <p className="text-[12px] text-muted-foreground mt-0.5">{item.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Primary Fixed 4-Tab Apple Bottom Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-card/90 backdrop-blur-xl border-t border-border px-4 pt-1.5 pb-[max(0.75rem,env(safe-area-inset-bottom))] transition-colors shadow-lg">
        <div className="flex items-center justify-around max-w-md mx-auto">
          {primaryTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`flex flex-col items-center justify-center py-1 px-3 rounded-full transition-all cursor-pointer min-w-[64px] min-h-[48px] active:scale-95 ${
                  isActive
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <div
                  className={`p-1.5 rounded-full transition-colors ${
                    isActive ? 'bg-primary/10' : 'bg-transparent'
                  }`}
                >
                  {tab.icon}
                </div>
                <span className={`text-[10px] mt-0.5 tracking-tight ${isActive ? 'font-semibold' : 'font-normal'}`}>
                  {tab.label}
                </span>
              </button>
            );
          })}

          {/* 4th Tab: More Button */}
          <button
            onClick={() => setIsMoreOpen(true)}
            className={`flex flex-col items-center justify-center py-1 px-3 rounded-full transition-all cursor-pointer min-w-[64px] min-h-[48px] active:scale-95 ${
              isMoreActive
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <div
              className={`p-1.5 rounded-full transition-colors ${
                isMoreActive ? 'bg-primary/10' : 'bg-transparent'
              }`}
            >
              <MoreHorizontal className="h-5 w-5" />
            </div>
            <span className={`text-[10px] mt-0.5 tracking-tight ${isMoreActive ? 'font-semibold' : 'font-normal'}`}>
              More
            </span>
          </button>
        </div>
      </div>
    </>
  );
};
